import type { SeatStatus } from "@synapse/engine";
import { create } from "zustand";
import { PathDisplayMode, PotentialRarestMove } from "../gameLogic";
import { deriveStreak } from "../streak";
import type {
  GameReport,
  MoveRecord,
  MoveJudgement,
  BacktrackReportEntry,
} from "../../utils/gameReportUtils";

// Core gameplay state - just the active game session
export interface GameplayState {
  // Current game state
  startWord: string | null;
  targetWord: string | null;
  currentWord: string | null;
  playerPath: string[];
  /** The gold path, fixed at game start (persisted key; Ruling R6). */
  optimalPath: string[];
  /** The Local Optimal route from the current word (persisted key; Ruling R6). */
  suggestedPathFromCurrent: string[];
  aiPath: string[];
  aiModel: string | null;
  /** Her seat this game (spec §3.2), or null when she is not playing it. */
  ariadneStatus: SeatStatus | null;

  // Game status and tracking
  gameStatus: "idle" | "loading" | "playing" | "won" | "given_up";
  pathDisplayMode: PathDisplayMode;

  // Game analytics
  gameReport: GameReport | null;
  /** Move judgements on the surviving path (persisted key; Ruling R6). */
  optimalChoices: MoveJudgement[];
  /** Consecutive Local Optimal moves, derived from optimalChoices. */
  streak: number;
  backtrackHistory: BacktrackReportEntry[];
  /** Every move made, in order; a backtrack never truncates it. */
  moveHistory: MoveRecord[];
  potentialRarestMovesThisGame: PotentialRarestMove[];

  // Game type flags
  isChallenge: boolean;
  isDailyChallenge: boolean;
  isTutorial: boolean;
  currentDailyChallengeId?: string;

  // Actions
  setGameState: (state: Partial<GameplayState>) => void;
  setPathDisplayMode: (mode: Partial<PathDisplayMode>) => void;
  addMoveJudgement: (judgement: MoveJudgement) => void;
  addBacktrackEvent: (event: BacktrackReportEntry) => void;
  addMoveRecord: (move: MoveRecord) => void;
  updatePotentialRarestMoves: (moves: PotentialRarestMove[]) => void;
  setGameReport: (report: GameReport | null) => void;
  resetGameState: () => void;
}

const initialState = {
  startWord: null,
  targetWord: null,
  currentWord: null,
  playerPath: [],
  optimalPath: [],
  suggestedPathFromCurrent: [],
  aiPath: [],
  aiModel: null,
  ariadneStatus: null,
  gameStatus: "idle" as const,
  pathDisplayMode: {
    player: true,
    optimal: false,
    suggested: false,
    ai: false,
  },
  gameReport: null,
  optimalChoices: [],
  streak: 0,
  backtrackHistory: [],
  moveHistory: [],
  potentialRarestMovesThisGame: [],
  isChallenge: false,
  isDailyChallenge: false,
  isTutorial: false,
  currentDailyChallengeId: undefined,
};

export const useGameplayStore = create<GameplayState>((set, _get) => ({
  ...initialState,

  setGameState: (newState) =>
    set(
      newState.optimalChoices
        ? {
            ...newState,
            streak: deriveStreak(newState.optimalChoices).streak,
          }
        : newState,
    ),

  setPathDisplayMode: (modeUpdate) =>
    set((state) => ({
      pathDisplayMode: { ...state.pathDisplayMode, ...modeUpdate },
    })),

  addMoveJudgement: (judgement) =>
    set((state) => {
      const optimalChoices = [...state.optimalChoices, judgement];
      return { optimalChoices, streak: deriveStreak(optimalChoices).streak };
    }),

  addBacktrackEvent: (event) =>
    set((state) => ({
      backtrackHistory: [...state.backtrackHistory, event],
    })),

  addMoveRecord: (move) =>
    set((state) => ({ moveHistory: [...state.moveHistory, move] })),

  updatePotentialRarestMoves: (moves) =>
    set({ potentialRarestMovesThisGame: moves }),

  setGameReport: (report) => set({ gameReport: report }),

  // The thread toggle's preference (spec D4) belongs to the player, not the
  // game: it survives a reset. It is seeded from storage at load.
  resetGameState: () =>
    set((state) => ({
      ...initialState,
      pathDisplayMode: {
        ...initialState.pathDisplayMode,
        ai: state.pathDisplayMode.ai,
      },
    })),
}));
